import { Modal } from '@design-system/components/modal/modal';
import { ChallengeDetailsVM } from '../view-model/ChallengeDetails.viewModel';
import { _noop } from '@client/new/helpers/general';
import {
  SubmissionCardMode,
  SubmissionVM,
} from '../view-model/Submission.viewModel';
import { Col, Row } from '@design-system/components/grid/grid';
import { ContentRowRenderer } from './components/ScrollableContent';
import * as styles from './Submission.view.css';
import { Typography } from '@design-system/components/typography/typography';
import { FavButton } from './components/FavButton';
import { F0 } from '@client/new/helpers/types';
import { SelectableButton } from './components/SelectableButton';
import { GalleryWithThumbnails } from './components/common/GalleryWithThumbnails';
import { Media } from './components/common/Media';
import { SubmissionMedia } from '../model/Challenges.types';
import { IconButton } from '@design-system/components/button/iconButton';
import React from 'react';
import { faArrowDownToLine } from '@fortawesome/pro-light-svg-icons';

const MediaGallery = (p: {
  title?: React.ReactNode;
  previewHeight?: number;
  name: string;
  media: SubmissionMedia[];
  width?: string;
}) => (
  <>
    {p.media.length > 0 ? (
      <GalleryWithThumbnails
        title={p.title}
        media={p.media}
        previewHeight={p.previewHeight}
        className={styles.gallery}
        maxWidth={p.width || '380px'}
      />
    ) : (
      <div style={{ marginTop: 110 }}>
        <Media
          type="none"
          value={`No ${p.name.toLocaleLowerCase()} media`}
          height="514px"
          width={p.width || '380px'}
        />
      </div>
    )}
  </>
);

export const MediaSelectButton = (
  p: SubmissionVM & { name: SubmissionCardMode },
) => (
  <SelectableButton
    minWidth={266}
    onClick={() => p.changeMode(p.name)}
    isSelected={p.mode === p.name}
  >
    {p.name}
  </SelectableButton>
);

const Galleries = (p: SubmissionVM) => (
  <Col gap="l" style={{ minHeight: 680 }}>
    <Row alignCenter justifyCenter>
      <MediaSelectButton {...p} name="Before/after" />
      <MediaSelectButton {...p} name="Transformation" />
    </Row>

    <Row
      gap="l"
      alignCenter
      justifyCenter
      style={{ height: '100%' }}
      responsive
    >
      {p.mode === 'Before/after' ? (
        <>
          <MediaGallery
            media={p.beforeMedia}
            name="Before"
            title={<ContentRowRenderer data={p.beforeMediaTitle} />}
          />
          <MediaGallery
            media={p.afterMedia}
            name="After"
            title={<ContentRowRenderer data={p.afterMediaTitle} />}
          />
        </>
      ) : (
        <MediaGallery
          name="transformation"
          previewHeight={553}
          media={p.transformationMedia}
          width="513px"
        />
      )}
    </Row>
  </Col>
);
export const SubmissionView = (
  p: SubmissionVM & { onFavClick: F0; onDownload: F0 },
) => {
  return (
    <Col gap="l">
      <Galleries {...p} />
      <Row className={styles.titleRow}>
        <Typography variant="headline" size="medium">
          {p.title}
        </Typography>
        <FavButton
          isFav={p.isFav}
          onClick={p.onFavClick}
          isLoading={p.isFavLoading}
        />
      </Row>
      <Row
        style={{
          gridTemplateColumns: '1fr auto',
          display: 'grid',
          alignItems: 'center',
        }}
      >
        <Col>
          <ContentRowRenderer data={p.metaLine} gap="xl" />
        </Col>
        <Col>
          {p.showDownload && (
            <IconButton
              icon={faArrowDownToLine}
              onClick={p.onDownload}
              color="primaryTonal"
            >
              Download
            </IconButton>
          )}
        </Col>
      </Row>
      <Col gap="l" className={styles.desc}>
        {p.desc.map((cr, i) => (
          <ContentRowRenderer data={cr} gap="xl" key={i} />
        ))}
      </Col>
    </Col>
  );
};

export const SubmissionModal = (p: ChallengeDetailsVM) => (
  <Modal
    isOpen={Boolean(p.selectedSubmission)}
    onClose={() => p.dispatch('CloseModal')}
    modalClassName={styles.modal}
  >
    {p.selectedSubmission ? (
      <SubmissionView
        {...p.selectedSubmission}
        onFavClick={() =>
          p.dispatch('ToggleFav', p.selectedSubmission?.submissionId || '')
        }
        onDownload={() => p.dispatch('DownloadZIP')}
      />
    ) : null}
  </Modal>
);
